'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/getting-started/my-corner.mdx
 * (published at user-docs.totalaccess.co.za/getting-started/my-corner#managed-tasks)
 */

import { useEffect, useState } from 'react';
import { CheckSquare, Plus, Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/contexts/ThemeContext';
import {
  createManagedTaskFromReference,
  findManagedTaskForReference,
} from '@/lib/managedTaskReferences';
import userDataStore, { type SubordinateEmployee } from '@/lib/offline/userDataStore';
import TaskAssigneeModal from './TaskAssigneeModal';
import toast from 'react-hot-toast';

const WORK_ITEM_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isWorkItemUuid(value: unknown): value is string {
  return WORK_ITEM_UUID_REGEX.test(String(value || ''));
}

interface QuickAddTaskButtonProps {
  // Context of what we're adding
  referenceType?: string;      // 'work_item', 'invoice', 'contact', 'calendar_event', etc.
  referenceId?: string;         // ID of the referenced item
  referenceUrl?: string;        // Deep link to the item

  // Pre-fill data
  title?: string;               // Pre-filled title
  description?: string;         // Pre-filled description
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;

  // Metadata
  contextData?: Record<string, unknown>;

  // UI options
  variant?: 'icon' | 'button' | 'minimal';
  size?: 'sm' | 'md' | 'lg';
  showToast?: boolean;
  label?: string;

  // Callbacks
  onTaskAdded?: (taskId: string | number) => void;
  onError?: (error: string) => void;
}

export default function QuickAddTaskButton({
  referenceType,
  referenceId,
  referenceUrl,
  title,
  description,
  priority = 'medium',
  dueDate,
  contextData,
  variant = 'icon',
  size = 'md',
  showToast = true,
  label,
  onTaskAdded,
  onError
}: QuickAddTaskButtonProps) {
  const [adding, setAdding] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [isInManagedTasks, setIsInManagedTasks] = useState(false);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [subordinates, setSubordinates] = useState<SubordinateEmployee[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<number | null>(null);
  const { user } = useAuth();
  const { currentTheme } = useTheme();

  useEffect(() => {
    let cancelled = false;

    const loadSubordinates = async () => {
      if (!user?.client_id || !user?.id) {
        setSubordinates([]);
        return;
      }

      try {
        const userData = await userDataStore.getUserData(
          Number(user.client_id),
          user.id
        );
        if (userData?.subordinates) {
          setSubordinates(userData.subordinates);
        } else if (user.subordinates) {
          // Fallback to user object if offline store doesn't have data yet
          setSubordinates(user.subordinates);
        }
      } catch (error) {
        console.error('Failed to load subordinates:', error);
        // Fallback to user object on error
        if (user.subordinates) {
          setSubordinates(user.subordinates);
        } else {
          setSubordinates([]);
        }
      }
    };

    void loadSubordinates();

    return () => {
      cancelled = true;
    };
  }, [user?.client_id, user?.id, user?.subordinates]);

  useEffect(() => {
    let cancelled = false;

    const syncExistingState = async () => {
      if (!referenceType || !referenceId) {
        setIsInManagedTasks(false);
        return;
      }

      setCheckingExisting(true);
      try {
        const existingTask = await findManagedTaskForReference({
          referenceType,
          referenceId,
          assignedTo: user?.id,
        });

        if (!cancelled) {
          setIsInManagedTasks(Boolean(existingTask));
        }
      } catch {
        if (!cancelled) {
          setIsInManagedTasks(false);
        }
      } finally {
        if (!cancelled) {
          setCheckingExisting(false);
        }
      }
    };

    void syncExistingState();

    return () => {
      cancelled = true;
    };
  }, [referenceId, referenceType, user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleManagedTaskReferenceAdded = (event: Event) => {
      const detail = (event as CustomEvent<{ referenceType?: string; referenceId?: string }>).detail;
      if (detail?.referenceType === referenceType && detail?.referenceId === referenceId) {
        setIsInManagedTasks(true);
      }
    };

    window.addEventListener('managedTaskReferenceAdded', handleManagedTaskReferenceAdded as EventListener);
    return () => {
      window.removeEventListener('managedTaskReferenceAdded', handleManagedTaskReferenceAdded as EventListener);
    };
  }, [referenceId, referenceType]);

  const createTask = async (assigneeIds: number[]) => {
    try {
      setAdding(true);

      let resolvedReferenceUrl = referenceUrl;
      if (!resolvedReferenceUrl && isWorkItemUuid(referenceId)) {
        try {
          const permalinkResponse = await fetch(`/api/v1/work-items/${encodeURIComponent(referenceId)}/permalink`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          const permalinkResult = await permalinkResponse.json().catch(() => ({}));
          if (permalinkResponse.ok && permalinkResult?.success && permalinkResult?.url) {
            resolvedReferenceUrl = permalinkResult.url;
          }
        } catch (permalinkError) {
          console.warn('Failed to resolve work item permalink for quick add task:', permalinkError);
        }
      }

      const { taskId, alreadyExisted } = await createManagedTaskFromReference({
        referenceType,
        referenceId,
        referenceUrl: resolvedReferenceUrl,
        title: title || 'New task',
        description,
        priority,
        dueDate,
        contextData,
        assignees: assigneeIds,
      });

      setIsInManagedTasks(true);

      if (showToast) {
        if (alreadyExisted) {
          toast('This item is already in your Managed Tasks list', {
            duration: 2000,
            icon: 'ℹ️',
          });
        } else {
          toast.success('✓ Task added to Managed Tasks!', {
            duration: 3000,
            icon: '✅',
          });
        }
      }

      if (taskId) {
        onTaskAdded?.(taskId);
      }

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add task';
      console.error('Error adding task:', error);
      onError?.(message);
      if (showToast) {
        toast.error(message, {
          duration: 4000,
        });
      }
    } finally {
      setAdding(false);
    }
  };

  const handleQuickAdd = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // If already exists, show message
    if (isInManagedTasks) {
      if (showToast) {
        toast('This item is already in your Managed Tasks list', {
          duration: 2000,
          icon: 'ℹ️',
        });
      }
      return;
    }

    if (adding) return;

    // If user has subordinates, show assignee selection modal
    if (subordinates.length > 0) {
      setSelectedAssignee(user?.id ?? null);
      setShowAssigneeModal(true);
      return;
    }

    // No subordinates, proceed with self assignment
    await createTask(user?.id ? [user.id] : []);
  };

  const handleAssigneeConfirm = (assigneeIds: number[]) => {
    void createTask(assigneeIds);
  };

  // Size classes
  const iconSizeClass = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  const textSizeClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';
  const paddingClass = size === 'sm' ? 'p-1.5' : size === 'lg' ? 'p-3' : 'p-2';
  const buttonPaddingClass = size === 'sm' ? 'px-3 py-1.5' : size === 'lg' ? 'px-6 py-3' : 'px-4 py-2';
  const isBusy = adding || checkingExisting;

  // Variant: Icon button
  if (variant === 'icon') {
    return (
      <>
        <button
          onClick={handleQuickAdd}
          disabled={isBusy}
          className={`
            ${paddingClass} rounded-lg transition-all
            ${isInManagedTasks
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }
            ${isBusy ? 'opacity-50 cursor-not-allowed' : ''}
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
          title={isInManagedTasks ? 'Already in Managed Tasks' : (label || 'Add to Managed Tasks')}
          aria-label={isInManagedTasks ? 'Already in Managed Tasks' : (label || 'Add to Managed Tasks')}
        >
          {isBusy ? (
            <Loader2 className={`${iconSizeClass} animate-spin`} />
          ) : isInManagedTasks ? (
            <Check className={iconSizeClass} />
          ) : (
            <CheckSquare className={iconSizeClass} />
          )}
        </button>
        <TaskAssigneeModal
          open={showAssigneeModal}
          onOpenChange={setShowAssigneeModal}
          onConfirm={handleAssigneeConfirm}
        />
      </>
    );
  }

  // Variant: Full button
  if (variant === 'button') {
    return (
      <>
        <button
          onClick={handleQuickAdd}
          disabled={isBusy}
          style={{
            backgroundColor: isInManagedTasks ? '#10b981' : currentTheme.colors.primary,
            opacity: isBusy ? 0.7 : 1
          }}
          className={`
            flex items-center justify-center gap-2 ${buttonPaddingClass} ${textSizeClass}
            text-white rounded-lg font-medium
            transition-all hover:opacity-90
            ${isBusy ? 'cursor-not-allowed' : 'cursor-pointer'}
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
          title={isInManagedTasks ? 'Already in Managed Tasks' : undefined}
        >
          {isBusy ? (
            <>
              <Loader2 className={`${iconSizeClass} animate-spin`} />
              {checkingExisting ? 'Loading...' : 'Adding...'}
            </>
          ) : isInManagedTasks ? (
            <>
              <Check className={iconSizeClass} />
              In Managed Tasks
            </>
          ) : (
            <>
              <Plus className={iconSizeClass} />
              {label || 'Add to Managed Tasks'}
            </>
          )}
        </button>
        <TaskAssigneeModal
          open={showAssigneeModal}
          onOpenChange={setShowAssigneeModal}
          onConfirm={handleAssigneeConfirm}
        />
      </>
    );
  }

  // Variant: Minimal (text link)
  return (
    <>
      <button
        onClick={handleQuickAdd}
        disabled={isBusy}
        className={`
          ${textSizeClass} transition-colors
          ${isInManagedTasks
            ? 'text-green-600 font-medium hover:text-green-700'
            : 'text-gray-600 hover:text-gray-900 underline'
          }
          ${isBusy ? 'opacity-50 cursor-not-allowed' : ''}
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        {isBusy && <Loader2 className="inline w-3 h-3 animate-spin mr-1" />}
        {isInManagedTasks ? '✓ In Managed Tasks' : `+ ${label || 'Add to Managed Tasks'}`}
      </button>
      <TaskAssigneeModal
        open={showAssigneeModal}
        onOpenChange={setShowAssigneeModal}
        onConfirm={handleAssigneeConfirm}
      />
    </>
  );
}
